import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import EditActionButton from 'components/EditActionButton';
import { DropdownOption, mapToDropdownOptionArrayWithSep } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Radio from 'components/final-form/Radio';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import Select from 'components/final-form/Select';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import UpsertStockAdministrationForm from 'components/medicaments/stockaadministration/UpsertStockAdministrationForm';
import SearchResultComponent from 'components/SearchResultComponent';
import { FormApi, FormState } from 'final-form';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Dimmer, Grid, Icon, Loader, Message, Modal, Popup, RadioProps } from 'semantic-ui-react';
import { getMandateResources } from 'services/resourcesServices';
import { findStockAdministrationById, searchStockAdministration } from 'services/stockAdministrationServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { StockAdministrationSearchBy } from 'ts-types/api.enums';
import {
  ResourceDto,
  StockAdministrationDto,
  StockAdministrationSearchRequest,
  UpsertStockAdministrationDto
} from 'ts-types/api.types';

const StockAdministrationContainer = styled(PraxStyledSegment)`
  .low-quantity {
    background-color: #e6e6e6;
  }
`;

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

interface StockAdministrationFieldProps extends FormState {
  values: Partial<StockAdministrationSearchRequest>
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
}

interface State {
  foundMedicamentData: Array<StockAdministrationDto>;
  stockAdministration?: UpsertStockAdministrationDto;
  mandateResources: Array<ResourceDto>;
  mandateResourceOptions: Array<DropdownOption>;
  stockAdministrationId?: number;
  currentMedicamentId?: number;
  selectedResourceId?: number;
  addEditModalOpen: boolean;
  pageDataLoaded: boolean;
  fastEntry: boolean;
  errorMessage?: String;
  cancelTokenSource: CancelTokenSource;
}

class StockAdministrationView extends Component<Props, State> {

  private mounted: boolean = false;

  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();

  initialSearchValues: Partial<StockAdministrationSearchRequest> = {
    stockAdministrationSearchTerm: "",
    searchBy: StockAdministrationSearchBy.ALL
  };

  form?: FormApi;

  constructor(props: Props) {
    super(props);

    const {radioMap} = this;
    const {getMessage, currentUser} = props;
    const cancelTokenSource = axios.CancelToken.source();

    const searchByValues = Object.values(StockAdministrationSearchBy);
    const radioDefinitions = generateRadioButtonOptionsFromEnum(
        searchByValues, "stockAdministration.searchBy", getMessage);

    radioDefinitions.forEach((radioDefinition, index) => {
      radioMap.set(searchByValues[index], radioDefinition);
    });

    this.state = {
      foundMedicamentData: [],
      mandateResources: [],
      mandateResourceOptions: [],
      stockAdministration: undefined,
      stockAdministrationId: undefined,
      addEditModalOpen: false,
      pageDataLoaded: false,
      fastEntry: false,
      cancelTokenSource
    };

    const mandateId = currentUser!.userConfig.mandateId
    this.initialSearchValues.mandateId = mandateId;

    this.findAllMandateResources(mandateId, cancelTokenSource);
  }

  findAllMandateResources = (mandateId: number, cancelTokenSource: CancelTokenSource) => {
    getMandateResources(mandateId, cancelTokenSource)
        .then(resources => {

          const resourcesOptions = mapToDropdownOptionArrayWithSep(
              resources, ["description"], "id");

          const selectedResourceId = resources && resources.length > 0 ? resources[0].id : undefined;
          this.initialSearchValues = {
            ...this.initialSearchValues,
            resourceId: selectedResourceId
          };

          this.setState({
            mandateResources: resources,
            mandateResourceOptions: resourcesOptions,
            selectedResourceId: selectedResourceId,
            pageDataLoaded: true
          });
        });
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel("Operation canceled by user");
  }

  openAddEditModal = (fastEntry: boolean, id: number | undefined, medicamentId: number | undefined) => {
    const {cancelTokenSource} = this.state;

    if (fastEntry) {
      this.showModal(fastEntry, undefined, undefined, undefined)
    } else {
      if (id) {
        findStockAdministrationById(id, cancelTokenSource)
            .then(response => {
              this.showModal(fastEntry, id, response, medicamentId);
            })
            .catch((e) => this.handleError(e.response.data));
      } else {
        this.showModal(fastEntry, undefined, undefined, medicamentId);
      }
    }
  };

  showModal = (fastEntry: boolean,
               id?: number,
               stockAdministration?: UpsertStockAdministrationDto,
               medicamentId?: number) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        stockAdministration: stockAdministration,
        stockAdministrationId: id,
        currentMedicamentId: medicamentId,
        fastEntry: fastEntry
      };
    });
  };

  hideModal = () => {
    this.setState(({}: State) => ({
      addEditModalOpen: false,
      stockAdministration: undefined,
      stockAdministrationId: undefined,
      currentMedicamentId: undefined,
      fastEntry: false
    }));
  };

  onSaveUpsertForm = () => {
    this.fetchSearchData(this.initialSearchValues);
    this.hideModal();
  }

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: string[] = [];

    if (knownErrors.includes(errorCode)) {
      this.setErrorMessage(getMessage(`error.${errorCode}`))
    } else {
      this.setErrorMessage(getMessage('error.general'))
    }
  }

  setErrorMessage = (error?: String) => {
    this.setState({
      errorMessage: error
    })
  }

  fetchSearchData = (values: any) => {

    const {cancelTokenSource, selectedResourceId} = this.state;

    this.setState({pageDataLoaded: false});

    values.mandateId = this.props.currentUser!.userConfig.mandateId;

    let newValues = {...values};
    newValues.mandateId = this.props.currentUser!.userConfig.mandateId;
    newValues.resourceId = values.resourceId ? values.resourceId : selectedResourceId;
    newValues.fastEntry = false;

    const onFinally = () => {
      this.setState({
        pageDataLoaded: true
      })
    };

    searchStockAdministration(newValues, cancelTokenSource)
        .then(response =>
            this.setState({
              foundMedicamentData: response
            })
        )
        .catch((error) => this.handleError(error.response.data))
        .finally(onFinally);
  }

  handleChange = debounce(({values}: StockAdministrationFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    if (values.resourceId) {
      this.setState({selectedResourceId: values.resourceId})
      this.fetchSearchData(values);
    }
  }, 300);


  render(): React.ReactNode {
    const {pageDataLoaded} = this.state;
    return (
        <StockAdministrationContainer>

          <div>
            {this.renderSearchSegment()}
          </div>

          <StyledViewTableRow>
            {!pageDataLoaded
                ? this.renderLoader()
                : this.renderStockAdministrationSegment()}
          </StyledViewTableRow>

          {this.renderUpsertStockAdministrationModal()}
        </StockAdministrationContainer>
    );
  }

  renderSearchSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.initialSearchValues}
            subscription={{pristine: true, values: true, initialValues: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({form}: FormRenderProps): React.ReactNode => {
    const {radioMap} = this;

    const {getMessage} = this.props;

    const {
      errorMessage,
      foundMedicamentData,
      mandateResourceOptions,
      selectedResourceId
    } = this.state;

    this.form = form;

    const numResults = foundMedicamentData.length;

    const allOption = radioMap.get(StockAdministrationSearchBy.ALL);
    const inStockOption = radioMap.get(StockAdministrationSearchBy.IN_STOCK);
    const lowQuantityOption = radioMap.get(StockAdministrationSearchBy.LOW_QUANTITY);
    const notManagedOption = radioMap.get(StockAdministrationSearchBy.NOT_MANAGED);

    const disabled = !selectedResourceId;

    return (
        <PxGrid>
          {errorMessage && this.renderErrorSegment()}
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("stockAdministration.location")}:
            </Grid.Column>
            <Grid.Column width={2}>
              <Field
                  name="resourceId"
                  component={Select}
                  options={mandateResourceOptions}
                  fluid
              />
            </Grid.Column>
            <Grid.Column width={11} textAlign={'right'}>
              <Button type="button"
                      disabled={disabled}
                      onClick={() => this.openAddEditModal(true, undefined, undefined)}>
                {getMessage("stockAdministration.fastEntry")}
              </Button>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("search")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="stockAdministrationSearchTerm"
                  component={Input}
                  fluid
                  disabled={disabled}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}></Grid.Column>
            <Grid.Column>
              <Field
                  name="searchBy"
                  component={Radio}
                  radioDefinition={allOption}
                  disabled={disabled}
              />
            </Grid.Column>
            <Grid.Column>
              <Field
                  name="searchBy"
                  component={Radio}
                  radioDefinition={inStockOption}
                  disabled={disabled}
              />
            </Grid.Column>
            <Grid.Column>
              <Field
                  name="searchBy"
                  component={Radio}
                  radioDefinition={lowQuantityOption}
                  disabled={disabled}
              />
            </Grid.Column>
            <Grid.Column>
              <Field
                  name="searchBy"
                  component={Radio}
                  radioDefinition={notManagedOption}
                  disabled={disabled}
              />
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    );
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative onDismiss={() => this.setErrorMessage()}>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

  renderLoader = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">
            {getMessage("stockAdministration.loading")}
          </Loader>
        </Dimmer>
    );
  };

  renderStockAdministrationSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundMedicamentData} = this.state;

    return (
        <VirtualizedTable
            id="stockAdministrationMainTable"
            rowCount={foundMedicamentData.length}
            rowGetter={this.stockAdministrationRowGetter}
            rowRenderer={this.foundStockAdministrationRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                label: getMessage("stockAdministration.medicament.code"),
                dataKey: "medicamentCode"
              },
              {
                width: 250,
                flexGrow: 1,
                label: getMessage("stockAdministration.medicament.description"),
                dataKey: "medicamentDescription"
              },
              {
                width: 200,
                label: getMessage("stockAdministration.quantity"),
                dataKey: "quantity",
                cellRenderer: this.quantityCellRenderer
              },
              {
                width: 200,
                label: getMessage("stockAdministration.minimum"),
                dataKey: "minimum"
              },
              {
                width: 200,
                label: getMessage("stockAdministration.minimumQuantity"),
                dataKey: "minimumQuantity"
              },
              {
                width: 100,
                label: getMessage("actions"),
                dataKey: "id",
                cellRenderer: this.actionsCellRenderer
              }
            ]}
        />
    );
  };

  quantityCellRenderer = ({rowData}: any) => {

    const {getMessage} = this.props;

    const trigger = <Icon name="warning sign" color="red"/>;
    const content = getMessage("stockAdministration.lowOnStockPopupMsg");

    if (rowData.lowOnStock) {
      return (
          <React.Fragment>
            <Popup
                trigger={trigger}
                popperDependencies={[]}
                popperModifiers={{
                  preventOverflow: {
                    boundary: document.getElementById("stockAdministrationMainTable")
                  }
                }}
                content={content}
                flowing
                on="hover"
                size="large"
                position="left center"
                style={PopupStyle}
            />
            <span>{rowData.quantity}</span>
          </React.Fragment>
      );
    } else {
      return (
          <React.Fragment>
            {rowData.quantity}
          </React.Fragment>
      );
    }
  };

  actionsCellRenderer = ({rowData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage("msgEdit")}
              onConfirm={() => this.openAddEditModal(false, rowData.id, rowData.medicamentId)}
          />
        </React.Fragment>
    );
  };

  stockAdministrationRowGetter = ({index}: any) => {
    const {foundMedicamentData} = this.state;

    Object.assign(foundMedicamentData[index], {index: index + 1});

    return foundMedicamentData[index];
  };

  foundStockAdministrationRowRenderer = ({className, columns, index, key, style}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};
    const {foundMedicamentData} = this.state;

    const newClassName = foundMedicamentData[index].id ? className : className + " low-quantity"

    return (
        <div
            {...a11yProps}
            className={newClassName}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(false, foundMedicamentData[index].id, foundMedicamentData[index].medicamentId)}
        >
          {columns}
        </div>
    );
  }

  renderUpsertStockAdministrationModal = (): React.ReactNode => {
    const {getMessage, currentUser} = this.props;
    const {
      addEditModalOpen,
      stockAdministration,
      stockAdministrationId,
      currentMedicamentId,
      selectedResourceId,
      fastEntry
    } = this.state;

    const title = fastEntry ? "stockAdministration.extendedTitle" : "stockAdministration.title"

    return (
        <Modal open={addEditModalOpen} closeOnDimmerClick={false} onClose={() => this.hideModal()}>
          <Modal.Header>{getMessage(title)}</Modal.Header>
          <FormModalContentContainer>
            <UpsertStockAdministrationForm
                stockAdministration={stockAdministration}
                stockAdministrationId={stockAdministrationId}
                medicamentId={currentMedicamentId}
                mandateId={currentUser!.userConfig.mandateId}
                resourceId={selectedResourceId}
                fastEntry={fastEntry}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };
}

export default withMessages(withAuthContext(StockAdministrationView));
